import {
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Search, X, ChevronDown, Check } from 'lucide-react';

// ------------------------------------------------------------------
// Button
// ------------------------------------------------------------------
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconRight?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, iconRight, children, className = '', ...rest },
  ref,
) {
  const cls = [
    'btn',
    `btn-${variant}`,
    size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : '',
    !children ? 'btn-icon' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button ref={ref} className={cls} {...rest}>
      {icon}
      {children}
      {iconRight}
    </button>
  );
});

// ------------------------------------------------------------------
// Input / Field
// ------------------------------------------------------------------
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: 'md' | 'lg';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size, className = '', ...rest },
  ref,
) {
  return <input ref={ref} className={`input ${size === 'lg' ? 'input-lg' : ''} ${className}`} {...rest} />;
});

export function Field({
  label, hint, error, required, children,
}: { label?: string; hint?: string; error?: string | null; required?: boolean; children: ReactNode }) {
  return (
    <div className="field">
      {label && (
        <label className="field-label">
          {label}
          {required && <span aria-hidden="true" style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>}
        </label>
      )}
      {children}
      {hint && !error && <div className="field-hint">{hint}</div>}
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

export function SearchInput({
  value, onChange, placeholder = 'Suchen…', autoFocus, className = '',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}) {
  return (
    <div className={`input-search ${className}`}>
      <span className="icon-wrap" aria-hidden="true"><Search size={14} /></span>
      <input
        className="input"
        type="search"
        role="searchbox"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        aria-label={placeholder}
      />
      {value && (
        <button className="clear" onClick={() => onChange('')} aria-label="Suche löschen" type="button">
          <X size={12} />
        </button>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Card
// ------------------------------------------------------------------
export function Card({ children, className = '', style }: { children: ReactNode; className?: string; style?: React.CSSProperties }) {
  return <div className={`card ${className}`} style={style}>{children}</div>;
}
export function CardHeader({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return <div className="card-header"><h3>{children}</h3>{action}</div>;
}
export function CardBody({ children, className = '', style }: { children: ReactNode; className?: string; style?: React.CSSProperties }) {
  return <div className={`card-body ${className}`} style={style}>{children}</div>;
}

// ------------------------------------------------------------------
// Badge / Avatar / Switch
// ------------------------------------------------------------------
type BadgeTone = 'success' | 'warning' | 'danger' | 'info' | 'accent' | 'neutral' | 'outline';

export function Badge({ tone = 'neutral', dot, children, className = '' }: {
  tone?: BadgeTone; dot?: boolean; children: ReactNode; className?: string;
}) {
  return (
    <span className={`badge badge-${tone} ${className}`}>
      {dot && <span className="dot" />} {children}
    </span>
  );
}

export function Avatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' | 'xl' }) {
  const cls = size === 'sm' ? 'avatar avatar-sm' : size === 'lg' ? 'avatar avatar-lg' : size === 'xl' ? 'avatar avatar-xl' : 'avatar';
  return <span className={cls}>{(name || '?').slice(0, 2)}</span>;
}

export function Switch({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <button
      type="button"
      className={`switch ${on ? 'on' : ''}`}
      onClick={() => onChange(!on)}
      role="switch"
      aria-checked={on}
      aria-label={label}
    />
  );
}

// ------------------------------------------------------------------
// Modal
// ------------------------------------------------------------------
export function Modal({
  open, onClose, title, children, footer, size = 'md',
}: {
  open: boolean; onClose: () => void; title: string;
  children: ReactNode; footer?: ReactNode; size?: 'md' | 'lg';
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className={`modal ${size === 'lg' ? 'modal-lg' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className="modal-header">
          <h2 id="modal-title">{title}</h2>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-label="Schließen" type="button">
            <X size={16} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Chip / FilterMenu
// ------------------------------------------------------------------
export function Chip({
  active, onClick, onRemove, children,
}: { active?: boolean; onClick?: () => void; onRemove?: () => void; children: ReactNode }) {
  return (
    <span className={`chip ${active ? 'active' : ''}`} onClick={onClick}>
      {children}
      {onRemove && (
        <span className="x" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
          <X size={10} />
        </span>
      )}
    </span>
  );
}

interface FilterOption { value: string; label: string; dot?: string }

export function FilterMenu({
  label, icon, value, options, onChange, allowClear = true,
}: {
  label: string;
  icon?: ReactNode;
  value: string | null;
  options: FilterOption[];
  onChange: (v: string | null) => void;
  allowClear?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  const current = options.find((o) => o.value === value);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <span className={`chip ${value ? 'active' : ''}`} onClick={() => setOpen(!open)} style={{ cursor: 'pointer' }}>
        {icon}
        {label}{current ? `: ${current.label}` : ''}
        <ChevronDown size={11} style={{ marginLeft: 2 }} />
      </span>
      {open && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0,
            background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
            borderRadius: 10, boxShadow: 'var(--shadow-md)', padding: 4, minWidth: 200, zIndex: 20,
            animation: 'slideUp 180ms cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          {allowClear && (
            <button
              type="button"
              onClick={() => { onChange(null); setOpen(false); }}
              style={menuItemStyle(false)}
            >
              <span style={{ width: 10, height: 10 }} /> Alle
            </button>
          )}
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              style={menuItemStyle(value === opt.value)}
            >
              {opt.dot ? (
                <span className="sec-dot" style={{ background: opt.dot }} />
              ) : (
                <span style={{ width: 10, height: 10 }} />
              )}
              <span style={{ flex: 1 }}>{opt.label}</span>
              {value === opt.value && <Check size={14} style={{ color: 'var(--accent)' }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function menuItemStyle(active: boolean): React.CSSProperties {
  return {
    width: '100%', padding: '8px 10px', borderRadius: 6, textAlign: 'left',
    fontSize: 14, color: 'var(--text-primary)',
    display: 'flex', alignItems: 'center', gap: 8,
    background: active ? 'var(--accent-soft)' : 'transparent',
    border: 'none', cursor: 'pointer',
  };
}

// ------------------------------------------------------------------
// SectionTag
// ------------------------------------------------------------------
export function SectionTag({ color, name, small }: { color: string; name: string; small?: boolean }) {
  return (
    <span className="section-tag" style={small ? { fontSize: 11, padding: '1px 7px 1px 5px' } : undefined}>
      <span className="sec-dot" style={{ background: color }} />
      {name}
    </span>
  );
}

// ------------------------------------------------------------------
// KV row
// ------------------------------------------------------------------
export function KV({ k, children, divider }: { k: string; children: ReactNode; divider?: boolean }) {
  return (
    <div className={`kv-row ${divider ? 'divider' : ''}`}>
      <div className="k">{k}</div>
      <div className="v">{children}</div>
    </div>
  );
}

// ------------------------------------------------------------------
// EmptyState
// ------------------------------------------------------------------
export function EmptyState({
  icon, title, subtitle, action,
}: { icon: ReactNode; title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <div className="ic">{icon}</div>
      <h4>{title}</h4>
      {subtitle && <p>{subtitle}</p>}
      {action}
    </div>
  );
}

// ------------------------------------------------------------------
// Status pills
// ------------------------------------------------------------------
export function MembershipStatusPill({ status }: { status: string }) {
  const cls: Record<string, string> = {
    mitglied: 'status-mitglied', aktiv: 'status-aktiv',
    funktionaer: 'status-funktionaer', ruhend: 'status-ruhend',
    ausgetreten: 'status-ausgetreten',
  };
  const label: Record<string, string> = {
    mitglied: 'Mitglied', aktiv: 'aktiv', funktionaer: 'Funktionär',
    ruhend: 'ruhend', ausgetreten: 'ausgetreten',
  };
  return (
    <span className={`status-pill ${cls[status] ?? ''}`}>
      <span style={{ width: 5, height: 5, borderRadius: 999, background: 'currentColor', display: 'inline-block' }} />
      {label[status] ?? status}
    </span>
  );
}

export function MemberStatusPill({ status }: { status: string }) {
  const cls: Record<string, string> = {
    aktiv: 'status-aktiv',
    ausgetreten: 'status-ausgetreten',
    verstorben: 'status-ruhend',
  };
  return (
    <span className={`status-pill ${cls[status] ?? ''}`}>
      <span style={{ width: 5, height: 5, borderRadius: 999, background: 'currentColor', display: 'inline-block' }} />
      {status}
    </span>
  );
}
